import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ListSkeleton } from "../components/Skeleton";

interface SupplierFile {
  uuid: string;
  label: string;
  fileSizeBytes: number;
  fileMime: string;
  fileSha256: string;
  originalFilename: string;
}

interface SharedEvidenceItem {
  evidenceUuid: string;
  documentTitle: string;
  description: string | null;
  status: "Authorised";
  networkSharedAt: string | null;
  authorisedAt: string | null;
  updatedAt: string;
  supplier: { uuid: string; name: string; slug: string };
  files: SupplierFile[];
}

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export function SupplierEvidence() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { activeOrganisation } = useAuth();
  const canPin = activeOrganisation?.role === "Admin" || activeOrganisation?.role === "Contributor";

  const [items, setItems] = useState<SharedEvidenceItem[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [pinningUuid, setPinningUuid] = useState<string | null>(null);
  const [confirmPinUuid, setConfirmPinUuid] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ evidence: SharedEvidenceItem[] }>("/network/supplier-evidence");
      setItems(res.evidence);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const pin = async (uuid: string) => {
    setPinningUuid(uuid);
    setErrors([]);
    try {
      const res = await api<{ evidenceUuid: string }>(`/network/supplier-evidence/${uuid}/pin`, {
        method: "POST",
      });
      navigate(`/o/${slug}/vault/${res.evidenceUuid}`);
    } catch (err) {
      setErrors(formatApiError(err));
      setPinningUuid(null);
      setConfirmPinUuid(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={`/o/${slug}/network`}
          className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
        >
          ← Network
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Supplier evidence
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Authorised evidence published by your active suppliers. &ldquo;Pin to my Vault&rdquo;
          creates an immutable byte-level copy in your own vault, owned by you, that survives the
          supplier revoking the connection or deleting the original.
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {items === null ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
          No evidence shared with you yet. Either you have no active supplier connections, or none
          of your suppliers have published any authorised evidence to the network.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li
              key={item.evidenceUuid}
              className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                    {item.supplier.name} <span className="font-mono">/{item.supplier.slug}</span>
                  </p>
                  <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {item.documentTitle}
                  </h2>
                  {item.description ? (
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      {item.description}
                    </p>
                  ) : null}
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Authorised {formatDateUk(item.authorisedAt)} · Published{" "}
                    {formatDateUk(item.networkSharedAt)} · Updated {formatDateUk(item.updatedAt)}
                  </p>
                </div>
                {canPin ? (
                  confirmPinUuid === item.evidenceUuid ? (
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <p className="text-xs text-slate-600 dark:text-slate-400">
                        Pin a copy to your vault?
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => void pin(item.evidenceUuid)}
                          disabled={pinningUuid !== null}
                          className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                        >
                          {pinningUuid === item.evidenceUuid ? "Pinning…" : "Yes, pin it"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmPinUuid(null)}
                          disabled={pinningUuid !== null}
                          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmPinUuid(item.evidenceUuid)}
                      className="shrink-0 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      Pin to my Vault
                    </button>
                  )
                ) : null}
              </div>

              {item.files.length > 0 ? (
                <ul className="mt-3 divide-y divide-slate-100 rounded border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
                  {item.files.map((f) => (
                    <li
                      key={f.uuid}
                      className="flex items-center justify-between gap-3 p-2 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-700 dark:text-slate-200">
                          {f.label}
                        </p>
                        <p className="truncate text-slate-500 dark:text-slate-400">
                          {f.originalFilename} · {formatBytes(f.fileSizeBytes)} ·{" "}
                          <span title={`SHA-256: ${f.fileSha256}`} className="font-mono">
                            {f.fileSha256.slice(0, 12)}…
                          </span>
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
